import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PolicyDataService } from '../shared/policy-data.service';
import { DocumentService } from '@core/services/document.service';

@Injectable({
  providedIn: 'root',
})
export class PdfGeneratorService {

  private pdfBlob: Blob;

  constructor(
    private policyDataService: PolicyDataService,
    private documentService: DocumentService
  ) { }

  get province() {
    return this.policyDataService.getProvince();
  }

  get policyId() {
    return this.policyDataService.getPolicyID();
  }

  generatePdf(rating: any) {

    const doc: any = new jsPDF();
    const logoUrl = "../../../../../assets/img/Sandbox_Logo_Boxed_LRG.png";
    const img = new Image();
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margins = { top: 20, bottom: 20, left: 12, right: 12 };
    const contentWidth = pageWidth - margins.left - margins.right;

    img.crossOrigin = "anonymous";
    img.src = logoUrl;

    const onload = () => {
      doc.addImage(img, "PNG", margins.left, margins.top - 10, 70, 26);
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.setFontSize(12);
      doc.rect(margins.left, 50, contentWidth, 40);
      doc.text(rating.Broker, 105, margins.top - 5, { maxWidth: 90 });
      doc.text("Minimum Policy Premium", margins.left + 2, 80);
      doc.text("Minimum Retained Premium", margins.left + 2, 85);
      doc.text(rating.Premium.MinimumPolicy + "/year", pageWidth - margins.right - 50, 80, { align: "left" });
      doc.text(rating.Premium.MinimumRetained + "/year", pageWidth - margins.right - 50, 85, { align: "left" });
      doc.setFont(undefined, "bold")
      doc.text("Quote Reference #" + rating["Reference#"], margins.left + 2, 55);
      doc.setFont(undefined, "normal")
      doc.setFontSize(10);
      doc.text("Policy Effective Date: " + rating.EffectiveDate + " Effective Time: " + rating.EffectiveTime, 105, 55, { align: "left" });
      doc.text("Policy Expiry Date: " + rating.ExpiryDate + " Expiry Time: " + rating.ExpiryTime, 105, 60, { align: "left" });
      doc.setFont(undefined, "bold")
      doc.setFontSize(16);
      doc.text("Total Estimated Premium", margins.left + 2, 72);
      doc.text(rating.Premium.Total + "/year", pageWidth - margins.right - 50, 72, { align: "left" });
      doc.setFont(undefined, "normal")

      // ========================================
      // ======== Applicant Details section
      // ========================================

      const applicantDetails = [
        [rating.Name.label, rating.Name.value],
        [rating.MailingAddress.label, rating.MailingAddress.value],
        [rating.Email.label, rating.Email.value],
        [rating.PhoneNumber.label, rating.PhoneNumber.value],
        ["Have you or any other regular driver on this policy:", ""],
        ["1. Been refused auto insurance?", rating.PolicyRefusedInsuranceInd.value],
        ["2. Had an auto insurance policy cancelled?", rating.Cancelled.value],
        [this.province === "MB" ? "3. Had a policy which was surcharged or coverage limited by a previous insurer?" : "3. Had a policy which was surcharged?", rating.PolicyInsurerSurchargeInd.value],
        [this.province === "MB" ? "" : "4. Has there been a Prior Carrier for this insured?", this.province === "MB" ? "" : rating.PolicyPreviousInsurerCd.value]
      ];

      autoTable(doc, {
        headStyles: { fillColor: [192, 234, 241], textColor: [0, 0, 0] },
        showHead: "firstPage",
        startY: 100,
        margin: margins,
        head: [["Applicant Details", ""]],
        body: applicantDetails,
        didParseCell: function (data) {
          if (data.row.index === 0 && data.row.section === "head") {
            data.cell.styles.fontSize = 14;
            data.cell.styles.fontStyle = "bold";
          }
        }
      });

      // ========================================
      // ======== Registered Owners (Drivers) section
      // ========================================

      for (const [i, driver] of (rating.Drivers ?? []).entries()) {
        const ownerOperator = [
          [`Registered Owner #${i + 1}`],
          ["Name", driver.Name],
          [{ content: "      I, the applicant, hereby authorize Saskatchewan Government Insurance to disclose all details of my driving record, including accidents, convictions and suspensions, and/or the driving record(s), including accidents, convictions and suspensions of the stated principal operators, from whom I declare I have obtained consent for these purposes, to Sandbox (Sandbox Mutual Insurance). Personal information is collected, used and disclosed by Sandbox in accordance with our Privacy Policy.", colSpan: 2 }, ""],
          ["Customer number", driver.CustomerNumber],
          ["Validation number", driver.ValidationNumber],
          ["Birth date", driver.BirthDate],
          ["Consecutive years driving experience", driver.YrsDriving]
        ]
        if (this.province === "MB") { //Remove consent for MB
          ownerOperator.splice(2, 1);
        }

        autoTable(doc, {
          body: ownerOperator,
          showHead: "firstPage",
          head: [["Registered Owner & Principal Operator Details", ""]],
          headStyles: { fillColor: [192, 234, 241], textColor: [0, 0, 0] },
          margin: { ...margins, top: 0, bottom: 0 },
          didParseCell: (data) => {
            if (data.row.index === 0) {
              data.cell.styles.fontSize = 14;
              data.cell.styles.fontStyle = "bold";
            } else if (data.row.index === 2) { //consent
              data.cell.styles.fontSize = 10;
            }
          },
          didDrawCell: (data) => {
            if (this.province === "MB" || data.row.index !== 2) return 
            //consent checkbox
            this.drawCheckbox(doc, data.row.cells[0], driver.LookupConsent)
          }
        });

        const valueColumnX = doc.lastAutoTable.body[1].cells[1].x

        // Driver convictions
        this.renderConditionalSubTable(driver.ConvictionsLast5Yrs, doc, { 
          rowLabel: "Has this driver had any convictions in the last 5 years?",
          entityLabel: "Convictions", valueColumnX, rowFill: [255, 255, 255]
        })
        
        // Driver suspensions
        this.renderConditionalSubTable(driver.SuspendedLast5Yrs, doc, { 
          rowLabel: "Has this driver ever been suspended in the last 5 years? ",
          entityLabel: "Suspension description", valueColumnX, rowFill: [245, 245, 245]
        })
        
        // Driver claims
        this.renderConditionalSubTable(driver.ClaimsLast5Yrs, doc, { 
          rowLabel: "Has this driver had any accidents/claims in the last 5 years?",
          entityLabel: "Claim description", valueColumnX, rowFill: [255, 255, 255]
        })
      }

      // ========================================
      // ======== Vehicles section
      // ========================================

      for (const [i, vehicle] of (rating.Locations[0].Vehicles ?? []).entries()) {
        const vehicleData = [
          [`Vehicle #${i + 1}`],
          ["Plate number", vehicle.Plate],
          [{ content: "      I hereby authorize Saskatchewan Government Insurance to disclose all details of my vehicle(s), including make, model and VIN to Sandbox (Sandbox Mutual Insurance). Personal information is collected, used and disclosed by Sandbox in accordance with our Privacy Policy. \nIf the vehicle doesn’t have a license plate, you must select No.", colSpan: 2 }, ""],
          ["Vehicle Type", vehicle.Type],
          ["Vehicle Description", vehicle.Name],
          ["Vehicle Identification Number (VIN)", vehicle.VIN],
          ["Is this vehicle leased?", vehicle.Leased],
          ["Is this vehicle registered to a business or used one or more days per week for business or professional activities?", vehicle.BusinessUse],
          ["Is this vehicle licensed?", vehicle.Licensed],
          ["Is this vehicle in storage?", vehicle.InStorage]
        ]
        if (this.province === "MB") { //Remove consent for MB
          vehicleData.splice(2, 1);
        }

        // Add Vehicle Use for MB PPV
        if (this.province === "MB" && vehicle.TypeCd === "PPV") {
          const typeIdx = vehicleData.findIndex(row => row[0] === "Vehicle Type");
          if (typeIdx >= 0) {
            vehicleData.splice(typeIdx + 1, 0, ["Vehicle Use", vehicle.VehicleUse]);
          }
        }

        autoTable(doc, {
          startY: (doc as any).autoTable.previous.finalY + 10,
          head: [["Vehicle Details", ""]],
          headStyles: { fillColor: [192, 234, 241], textColor: [0, 0, 0] },
          body: vehicleData,
          showHead: i == 0 ? "firstPage" : "never",
          margin: margins,
          didParseCell: function (data) {
            if (data.row.index === 0) {
              data.cell.styles.fontSize = 14;
              data.cell.styles.fontStyle = "bold";
            } else if (data.row.index === 2) { //consent
              data.cell.styles.fontSize = 10;
            }
          },
          didDrawCell: (data) => {
            if (this.province === "MB" || data.row.index !== 2) return 
            //consent checkbox
            this.drawCheckbox(doc, data.row.cells[0], vehicle.LookupConsent)
          }
        });

        //Driver Assignments
        autoTable(doc, {
          showHead: "firstPage",
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
          head: [['Driver Assignment', '']],
          body: vehicle.Assignments.map((as) => [as.Type, as.Name]),
          startY: doc.autoTable.previous.finalY + 10,
          margin: margins,
        });

        // coverages
        autoTable(doc, {
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
          head: [['Coverage', 'Deductible/Limit', 'Premium']],
          body: vehicle.Coverages.map((v) => [v.Name, `${v.Deductible} / ${v.Limit}`, v.Premium]),
          startY: doc.autoTable.previous.finalY + 10,
          margin: margins,
        });
      }

      // ========================================
      // ======== Consent section
      // ========================================

      let currentY = doc.autoTable.previous.finalY;
      if (currentY + 195 > pageHeight - margins.bottom) {
        doc.addPage();
        currentY = margins.top;
      }

      //Title
      doc.setFontSize(14);
      doc.setFont(undefined, "bold")
      doc.text("Legal Statement", margins.left + 2, currentY + 5);
      doc.setFontSize(10);
      doc.setFont(undefined, "normal")

      //Outer box
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.setFontSize(10);
      const consentBoxY = currentY + 15;
      const consentBoxHeight = 200;
      doc.rect(margins.left, consentBoxY, contentWidth, consentBoxHeight);

      const consentText = [
        ["Where (a) an Applicant for a contract, (i) gives false particulars of the described automobile to be insured to the prejudice of the Insurer, or (ii) knowingly misrepresents or fails to disclose in the application any fact required to be stated therein; or (b) the Insured contravenes a term of the contract or commits a fraud; or (c) the Insured willfully makes a false statement in respect of a claim under the contract, a claim by the Insured is invalid and the right of the Insured to recover indemnity is forfeited.", ""],
        ["", ""],
        [`CONSENT:${this.province === "SK" ? " I, the applicant, acknowledge that:" : ""}`, ""],
        ["", ""],
        [`1. ${this.province === "SK" ? "All of the information given in this application is true and I hereby apply for a contract of automobile insurance based on the truth of the said information. I authorize you to collect, use and disclose information as permitted by law for the purposes necessary to assess the risk, issue or decline the insurance contract, renewal or change, detect and prevent fraud and investigate and settle any claims. Unless otherwise authorized or permitted by law, information collected for the purposes stated in this consent shall be limited to my driving record information, automobile insurance claims history and premium payment information and the driving record information, automobile insurance claims history and premium payment information of the stated principal operator(s) for who I declare I have obtained consent for these purposes. Personal Information is collected, used and disclosed by Sandbox in accordance with our Privacy Policy." : "I am applying for automobile insurance based on the information provided above. With respect to this application or any renewal or change to my coverage, I authorize you to collect, use and disclose information as permitted by law for the purposes necessary to assess the risk and issue or decline the insurance contract. Information collected for the purposes stated in this consent shall be limited to my driving record information, automobile insurance claims history and premium payment information unless otherwise authorized or permitted by law."}`, ""],
        ["", ""],
        [`2. ${this.province === "SK" ? "I hereby authorize Saskatchewan Government Insurance to disclose all details of my driving record, including accidents, convictions and suspensions, and all details of the driving record(s), of the stated principal operators(s), including accidents, convictions and suspensions, to Sandbox (Sandbox Mutual Insurance)." : "I authorize Sandbox Mutual Insurance to obtain from Manitoba Division of Driver and Vehicle Licensing a copy of the applicant's driving records."}`, ""],
        ["", ""],
        ["3. I acknowledge that the Total Estimated Premium is subject to adjustment to the Insurer's manual premium for the risk.", ""],
        ["", ""],
        ["The Agency/Broker agrees that:", ""],
        ["", ""],
        ["1. This document is being sent in accordance with the Agency/Broker Agreement and any Addendums to the Agreement.", ""],
        ["2. This document is considered to be 'in writing' and to have been signed by the applicant.", ""],
        ["3. The Agency/Broker is responsible for obtaining and maintaining the original signed application form.", ""],
        ["4. Sandbox reserves the right to conduct an audit and physical inspection at the Agency/Broker's place of business of all records and documents required to be maintained and stored by the Agency/Broker, including the original signed application form.", ""]
      ];

      autoTable(doc, {
        body: consentText,
        startY: currentY + 20,
        margin: margins,
        styles: {
          fontSize: 10,
          fillColor: false
        },
        theme: 'plain',
        bodyStyles: {
          fillColor: false
        },
        columnStyles: {
          0: {
            cellWidth: 170,
            fillColor: false
          }
        },
        didParseCell: function(data) {
          const [text] = data.cell.text
          if (!text) return
          if (/^\d/.test(text)) { //indent list items
            data.cell.styles.cellPadding = { left: 12 };
          } else if (data.row.index > 0 && !/^\s*$/.test(text)) { //bold headings
            data.cell.styles.fontStyle = 'bold';
          }
          if (text.includes('agrees')) { //room for checkbox
            data.cell.styles.cellPadding = { left: 9 };
          } else if (text.includes('1. This doc')) {
            data.cell.styles.cellPadding = { ...data.cell.styles.cellPadding as {}, bottom: 4 };
          }
        }
      });
      const yCordByProv = { SK: 152, MB: 127 }
      this.drawCheckbox(doc, { x: margins.left + 5, y: currentY + yCordByProv[this.province] }, true)
      
      // ========================================
      // ======== Signatures section
      // ========================================
      let signaturesY;
      if(consentBoxY + consentBoxHeight + 20 > pageHeight - margins.bottom - 5) {
        doc.addPage();
        currentY = margins.top + 5;
        signaturesY = margins.top;
      }
      else {
        signaturesY = consentBoxY + consentBoxHeight + 20;
      }
      
      
      for (const y of [signaturesY - 20, signaturesY - 10, signaturesY]) {

        doc.text("APPLICANT'S SIGNATURE  X  _______________________________    DATE_______________________________ ", margins.left, currentY + y)
      }

      this.pdfBlob = doc.output("blob");
      this.documentService.putPolicyDocument(this.policyId, "application-form", this.pdfBlob).subscribe((res) => {});
    }

    img.onload = onload
  }

  openPdf() {
    const pdfUrl = URL.createObjectURL(this.pdfBlob);
    window.open(pdfUrl, "_blank");
  }

  private quoteSummaryBlob: Blob;

  generateQuoteSummaryPdf(rating: any, quoteNumber: string, quoteDescription: string, callback?: () => void) {
    const doc: any = new jsPDF();
    const logoUrl = "../../../../../assets/img/Sandbox_Logo_Boxed_LRG.png";
    const img = new Image();
    const pageHeight = doc.internal.pageSize.height;
    const margins = { top: 20, bottom: 20, left: 15, right: 15 };
    const isAuto = this.policyDataService.getLOB() === 'A';

    img.crossOrigin = "anonymous";
    
        const generatePdfContent = () => {
      try {
        if (img.complete && img.naturalWidth > 0) {
          const logoWidth = 70;
          const logoHeight = 26;
          const pageWidth = doc.internal.pageSize.width;
          const logoX = pageWidth - logoWidth - 15; 
          doc.addImage(img, "PNG", logoX, 15, logoWidth, logoHeight);
        }
      } catch (e) {
      }
      
      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.text("Quote Number:", 15, 20);
      doc.setFont(undefined, "normal");
      doc.text(quoteNumber, 15, 26);
      
      if (quoteDescription) {
        doc.setFont(undefined, "bold");
        doc.text("Quote Description:", 15, 34);
        doc.setFont(undefined, "normal");
        doc.text(quoteDescription, 15, 40);
      }
      
      // Premium
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text("Total Premium:", 15, 52);
      doc.text(rating.Premium?.Total || "$0.00", 170, 52, { align: "left"  });
      doc.setFont(undefined, "normal");
      doc.setFontSize(10);

      let startY = 65;

      //Liability section
      if (rating.Liability && rating.Liability.length > 0) {
        for (const liabilityItem of rating.Liability) {
          if (startY > pageHeight - 50) {
            doc.addPage();
            startY = margins.top;
          }

          autoTable(doc, {
            startY: startY,
            head: [["Liability Coverage", "", "", ""]],
            headStyles: { fillColor: [192, 234, 241], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 12 },
            // body: [[liabilityItem.Name || "", "", "", ""]],
            bodyStyles: { fontStyle: "bold", fontSize: 11 },
            theme: 'striped',
            margin: { top: margins.top, bottom: margins.bottom, left: margins.left, right: margins.right }
          });

          if (liabilityItem.Coverages && liabilityItem.Coverages.length > 0) {
            const coverageData = liabilityItem.Coverages.map(cov => [
              cov.Name || "",
              cov.Limit || "",
              cov.Deductible || "",
              cov.Premium || ""
            ]);

            autoTable(doc, {
              startY: doc.autoTable.previous.finalY + 2,
              head: [["Coverage", "Limit", "Deductible", "Premium"]],
              headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
              body: coverageData,
              theme: 'grid',
              margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
            });
            startY = doc.autoTable.previous.finalY + 2;
          }

          //Discounts
          if (liabilityItem.Discounts && liabilityItem.Discounts.length > 0) {
            const discountData = liabilityItem.Discounts.map(disc => [
              disc.Name || ""
            ]);

            autoTable(doc, {
              startY: startY,
              head: [["Discounts"]],
              headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
              body: discountData,
              theme: 'grid',
              margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
            });
            startY = doc.autoTable.previous.finalY + 2;
          }

          //Surcharges
          if (liabilityItem.Surcharges && liabilityItem.Surcharges.length > 0) {
            const surchargeData = liabilityItem.Surcharges.map(surch => [
              surch.Name || "",
              surch.Amount || "",
              surch.Percent || ""
            ]);

            autoTable(doc, {
              startY: doc.autoTable.previous.finalY + 5,
              head: [["Surcharges", "Amount", "Percent"]],
              headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
              body: surchargeData,
              theme: 'grid',
              margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
            });
            startY = doc.autoTable.previous.finalY + 2;
          }

          startY = doc.autoTable.previous.finalY;
        }
      }

      //Locations
      if (rating.Locations && rating.Locations.length > 0) {
        for (const [locIndex, location] of rating.Locations.entries()) {
          if (startY > pageHeight - 50) {
            doc.addPage();
            startY = margins.top;
          }

          const locationTitle = `Location #${locIndex + 1}: ${location.Address || 'N/A'}`;
          autoTable(doc, {
            startY: startY,
            head: [[locationTitle, "", "", ""]],
            headStyles: { fillColor: [192, 234, 241], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 12 },
            //body: [["", "", "", ""]],
            theme: 'striped',
            margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
          });

          startY = doc.autoTable.previous.finalY;

          //Loc Liability
          if (location.Liability && location.Liability.length > 0) {
            for (const liabilityItem of location.Liability) {
              if (startY > pageHeight - 50) {
                doc.addPage();
                startY = margins.top;
              }

              autoTable(doc, {
                startY: startY,
                head: [[`Liability: ${liabilityItem.Name || ''}`, "", "", ""]],
                headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 11 },
                theme: 'striped',
                margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
              });

              startY = doc.autoTable.previous.finalY;

              if (liabilityItem.Coverages && liabilityItem.Coverages.length > 0) {
                const coverageData = liabilityItem.Coverages.map(cov => [
                  cov.Name || "",
                  cov.Limit || "",
                  cov.Deductible || "",
                  cov.Premium || ""
                ]);

                autoTable(doc, {
                  startY: startY,
                  head: [["Coverage", "Limit", "Deductible", "Premium"]],
                  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                  body: coverageData,
                  theme: 'grid',
                  margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
                });
                startY = doc.autoTable.previous.finalY + 2;
              }

              if (liabilityItem.Discounts && liabilityItem.Discounts.length > 0) {
                const discountData = liabilityItem.Discounts.map(disc => [
                  disc.Name || ""
                ]);

                autoTable(doc, {
                  startY: startY,
                  head: [["Discounts"]],
                  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                  body: discountData,
                  theme: 'grid',
                  margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
                });
                startY = doc.autoTable.previous.finalY + 2;
              }

              if (liabilityItem.Surcharges && liabilityItem.Surcharges.length > 0) {
                const surchargeData = liabilityItem.Surcharges.map(surch => [
                  surch.Name || "",
                  surch.Amount || "",
                  surch.Percent || ""
                ]);

                autoTable(doc, {
                  startY: startY,
                  head: [["Surcharges", "Amount", "Percent"]],
                  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                  body: surchargeData,
                  theme: 'grid',
                  margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
                });
                startY = doc.autoTable.previous.finalY + 2;
              }

              startY = doc.autoTable.previous.finalY;
            }
          }

          //Floaters
          if (location.Floaters && location.Floaters.length > 0) {
            for (const floaterItem of location.Floaters) {
              if (startY > pageHeight - 50) {
                doc.addPage();
                startY = margins.top;
              }

              autoTable(doc, {
                startY: startY,
                head: [[`Floater: ${floaterItem.Name || ''}`, "", "", ""]],
                headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 11 },
                theme: 'striped',
                margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
              });

              startY = doc.autoTable.previous.finalY;

              if (floaterItem.Coverages && floaterItem.Coverages.length > 0) {
                const coverageData = floaterItem.Coverages.map(cov => [
                  cov.Name || "",
                  cov.Limit || "",
                  cov.Deductible || "",
                  cov.Premium || ""
                ]);

                autoTable(doc, {
                  startY: startY,
                  head: [["Coverage", "Limit", "Deductible", "Premium"]],
                  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                  body: coverageData,
                  theme: 'grid',
                  margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
                });
                startY = doc.autoTable.previous.finalY + 2;
              }

              if (floaterItem.Discounts && floaterItem.Discounts.length > 0) {
                const discountData = floaterItem.Discounts.map(disc => [
                  disc.Name || ""
                ]);

                autoTable(doc, {
                  startY: startY,
                  head: [["Discounts"]],
                  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                  body: discountData,
                  theme: 'grid',
                  margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
                });
                startY = doc.autoTable.previous.finalY + 2;
              }

              if (floaterItem.Surcharges && floaterItem.Surcharges.length > 0) {
                const surchargeData = floaterItem.Surcharges.map(surch => [
                  surch.Name || "",
                  surch.Amount || "",
                  surch.Percent || ""
                ]);

                autoTable(doc, {
                  startY: startY,
                  head: [["Surcharges", "Amount", "Percent"]],
                  headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                  body: surchargeData,
                  theme: 'grid',
                  margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
                });
                startY = doc.autoTable.previous.finalY + 2;
              }

              startY = doc.autoTable.previous.finalY;
            }
          }

          //Vehicles
          const risks = location.Risks || location.Vehicles || [];
          for (const [riskIndex, risk] of risks.entries()) {
            if (startY > pageHeight - 50) {
              doc.addPage();
              startY = margins.top;
            }

            const riskTitle = isAuto && risk.VIN 
              ? `VEHICLE: ${risk.VIN} - ${risk.Name || 'N/A'}`
              : `Risk: ${risk.Name || 'N/A'}`;
            
            autoTable(doc, {
              startY: startY,
              head: [[riskTitle, "", "", ""]],
              headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 11 },
              //body: [["", "", "", ""]],
              theme: 'striped',
              margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
            });

            startY = doc.autoTable.previous.finalY;

            //Coverages
            if (risk.Coverages && risk.Coverages.length > 0) {
              const coverageColumns = isAuto 
                ? ["Coverage", "Deductible/Limit", "Premium"]
                : ["Coverage", "Limit", "Deductible", "Basis of Settlement", "Premium"];
              
              const coverageData = risk.Coverages.map(cov => {
                if (isAuto) {
                  return [
                    cov.Name || "",
                    `${cov.Deductible || ""} / ${cov.Limit || ""}`,
                    cov.Premium || ""
                  ];
                } else {
                  return [
                    cov.Name || "",
                    cov.Limit || "",
                    cov.Deductible || "",
                    cov.BasisOfSettlement || "",
                    cov.Premium || ""
                  ];
                }
              });

              autoTable(doc, {
                startY: startY,
                head: [coverageColumns],
                headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                body: coverageData,
                theme: 'grid',
                margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
              });

              startY = doc.autoTable.previous.finalY;
            }

            //Vehicle Details
            if (risk.RiskDetails && risk.RiskDetails.FireProtection) {
              doc.setFontSize(10);
              doc.text(`Fire Protection: ${risk.RiskDetails.FireProtection}`, 25, startY);
              startY += 8;
            }

            //Assignments
            if (risk.Assignments && risk.Assignments.length > 0) {
              const assignmentData = risk.Assignments.map(assign => [
                assign.Name || "",
                assign.Type || ""
              ]);

              autoTable(doc, {
                startY: startY,
                head: [["Driver Assignment", "Role"]],
                headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                body: assignmentData,
                theme: 'grid',
                margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
              });

              startY = doc.autoTable.previous.finalY;
            }

            //Discounts
            if (risk.Discounts && risk.Discounts.length > 0) {
              const discountData = risk.Discounts.map(disc => [
                disc.Name || ""
              ]);

              autoTable(doc, {
                startY: startY,
                head: [["Discounts"]],
                headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                body: discountData,
                theme: 'grid',
                margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
              });

              startY = doc.autoTable.previous.finalY;
            }

            //Surcharges
            if (risk.Surcharges && risk.Surcharges.length > 0) {
              const surchargeData = risk.Surcharges.map(surch => [
                surch.Name || "",
                surch.Amount || "",
                surch.Percent || ""
              ]);

              autoTable(doc, {
                startY: startY,
                head: [["Surcharges", "Amount", "Percent"]],
                headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold" },
                body: surchargeData,
                theme: 'grid',
                margin: { left: margins.left, right: margins.right, top: margins.top, bottom: margins.bottom }
              });

              startY = doc.autoTable.previous.finalY;
            }
          }

          startY = doc.autoTable.previous.finalY;
        }
      }

      this.quoteSummaryBlob = doc.output("blob");
      if (callback) {
        callback();
      }
    };

    img.onload = generatePdfContent;
    img.onerror = generatePdfContent;
    img.src = logoUrl;
  }

  openQuoteSummaryPdf() {
    if (this.quoteSummaryBlob) {
      const pdfUrl = URL.createObjectURL(this.quoteSummaryBlob);
      window.open(pdfUrl, "_blank");
    }
  }

  private drawCheckbox(doc, cell, checked) {
    const checkboxUrl = "../../../../../assets/img/checkbox.png";
    const { x, y } = cell
    const img2 = new Image();
    img2.src = checkboxUrl;
    doc.addImage(img2, "PNG", x + 2, y + 2, 4, 4);
    
    if (!checked) {
      // Removing check mark
      const centerX = x + 2 + 4 / 2;
      const centerY = y + 2 + 4 / 2;

      const squareSize = 3;
      const squareX = centerX - squareSize / 2;
      const squareY = centerY - squareSize / 2;
      doc.setFillColor(245, 245, 245);
      doc.rect(squareX, squareY, squareSize, squareSize, "F");
    }
  }
  
  private renderConditionalSubTable(entities, doc, { rowLabel, entityLabel, valueColumnX, rowFill }) {
    function willDrawCell(data) {
      if (data.column.index === 1) {
        data.cell.x = valueColumnX
      }
    }
    const hasEntities = Array.isArray(entities)
    autoTable(doc, {
      body: [[rowLabel, hasEntities ? "Yes" : (["Yes", "No"].includes(entities) ? entities : "No")]],
      startY: doc.lastAutoTable.finalY,
      // Respect page margins; avoid negative right margins that cause overflow
      willDrawCell,
      didParseCell: function (data) {
        if (data.row.section === 'body') {
          data.cell.styles.fillColor = rowFill;
        }
      },
    });
    
    if (!hasEntities) return 

    autoTable(doc, {
      body: entities.map((e) => [e.Description, e.Date]),
      margin: { left: 20, bottom: 20 },
      head: [[`${entityLabel}`, "Date"]],
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
      startY: doc.lastAutoTable.finalY + 3,
      styles: {
        fontStyle: "italic",
        textColor: [0, 0, 0],
      },
      willDrawCell,
      didParseCell: function (data) {
        if (data.row.section === 'body') {
          data.cell.styles.fillColor = [255, 255, 255];
        }
      },
    });
  }
}